import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import {
  getNotifications, markNotifRead, markAllNotifRead,
} from "../lib/services";
import { formatApiError } from "../lib/api";

const NOTIF_LABEL = {
  follow: "started following you",
  log_like: "liked your log",
  log_comment: "commented on your log",
  credit_suggestion_approved: "your credit suggestion was approved",
  credit_suggestion_rejected: "your credit suggestion was rejected",
};

function destinationFor(n) {
  if (n.type === "follow" && n.actor?.username) return `/user/${n.actor.username}`;
  if ((n.type === "log_like" || n.type === "log_comment") && n.showId) {
    return `/show/${n.showId}`;
  }
  if ((n.type === "credit_suggestion_approved" || n.type === "credit_suggestion_rejected") && n.showId) {
    return `/show/${n.showId}`;
  }
  return null;
}

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await getNotifications();
      setItems(r.items || []);
      setCount(r.count || 0);
    } catch (err) {
      console.warn("notifications fetch failed:", err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleClick = async (n) => {
    if (!n.read) {
      try { await markNotifRead(n.id); } catch (err) {
        console.warn("mark read failed:", err);
      }
    }
    const dest = destinationFor(n);
    if (dest) navigate(dest);
    else refresh();
  };

  const readAll = async () => {
    try {
      await markAllNotifRead();
      toast("all read");
      refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  return (
    <main data-testid="notifications-page" className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <Link
          to="/home"
          className="font-mono text-xs lowercase text-white/60 hover:text-white inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> home
        </Link>
        <div className="flex items-baseline justify-between mt-3">
          <h1 className="font-display text-4xl sm:text-5xl leading-none">
            notifs<span className="text-acid">.</span>
          </h1>
          {items.some((n) => !n.read) && (
            <button
              data-testid="mark-all-read"
              onClick={readAll}
              className="font-mono text-[11px] lowercase border-2 border-acid text-acid px-2.5 py-1 hover:bg-acid hover:text-black inline-flex items-center gap-1 transition-colors"
            >
              <Check className="w-3 h-3" strokeWidth={2} /> mark all read
            </button>
          )}
        </div>
        <p className="font-mono text-[11px] text-white/50 mt-2 tabular-nums">
          {count} total
        </p>
      </header>

      {!loaded ? (
        <p className="px-5 py-10 text-center font-mono text-xs text-white/40">loading...</p>
      ) : items.length === 0 ? (
        <p className="px-5 py-16 text-center font-mono text-xs text-white/40">
          no notifications yet.
        </p>
      ) : (
        <ul>
          {items.map((n) => (
            <li key={n.id}>
              <button
                data-testid={`notif-${n.id}`}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-5 py-4 border-b border-white/10 flex items-start gap-3 hover:bg-acid/5 transition-colors ${
                  !n.read ? "bg-acid/[0.04]" : ""
                }`}
              >
                <span
                  className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                    n.read ? "bg-white/20" : "bg-acid"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-white/90">
                    {n.actor?.username ? (
                      <span className="text-acid">@{n.actor.username}</span>
                    ) : (
                      <span className="text-white/60">someone</span>
                    )}{" "}
                    <span>{NOTIF_LABEL[n.type] || n.text}</span>
                  </div>
                  <div className="font-mono text-[10px] text-white/40 mt-0.5">
                    {(n.createdAt || "").slice(0, 10)}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
